import moment from 'moment';
import { BiCategoryAlt } from 'react-icons/bi';
import { ImInsertTemplate } from 'react-icons/im';
import { IoEarth } from 'react-icons/io5';
import {
  PiArrowCounterClockwise,
  PiBellRingingLight,
  PiCards,
  PiCirclesThreePlusLight,
  PiCloudSunLight,
  PiConfettiLight,
  PiEnvelopeLight,
  PiGift,
  PiImagesSquare,
  PiLink,
  PiListMagnifyingGlassLight,
  PiMedal,
  PiNewspaperLight,
  PiPackageLight,
  PiPaperPlaneTilt,
  PiQueue,
  PiRocketLaunchLight,
  PiSealCheck,
  PiTranslate,
  PiTreeStructure,
  PiUpload,
} from 'react-icons/pi';
import { BsStickies } from 'react-icons/bs';
import { VscNotebookTemplate } from 'react-icons/vsc';
import { FEATURE_SHOW_OLD_NEWS } from '@/const/features';
import { Permissions } from '@/const/permissions';
import { EVENT_TYPES_MAP, SOURCES_MAP } from '@/const/sources';
import { DATE_FORMAT } from '@/const/system';
import { COLLECTIONS_SOURCES } from '@/domains/Content/pages/Collections/PageCollections/PageCollections.const';
import { FEEDS_SOURCE_MAP } from '@/domains/Content/pages/Feeds/PageFeeds.const';
import { FEATURES_SOURCE_MAP } from '@/domains/Featured/pages/PageFeatures/PageFeatures.const';
import { EVENTS_DICT_SOURCE } from '@/domains/Other/pages/Events/PageEvents/PageEvents.const';
import { IntlMessages } from '@/components';
import appRoutes from '@/configs/routes';

const pages = [
  {
    header: <IntlMessages id="sidebar-content" />,
    groupPermissions: [Permissions.CONTENT.IMAGES.LIST],
  },
  {
    id: 'content-feeds',
    title: <IntlMessages id="sidebar-content-feeds" />,
    icon: <PiCards size={18} />,
    groupPermissions: [
      Permissions.CONTENT.FEEDS.LIST,
      Permissions.CONTENT.FEEDS.PLANNING.BOARD.LIST,
      Permissions.CONTENT.FEEDS.RELEASED,
      Permissions.CONTENT.FEEDS.ARCHIVED,
      Permissions.CONTENT.FEEDS.TRASHED,
    ],
    children: [
      {
        id: 'content-feeds-planning-list',
        title: <IntlMessages id="sidebar-content-feeds-planning" />,
        navLink: appRoutes.urlPageFeeds({ feedSource: FEEDS_SOURCE_MAP.PLANNING }),
        permission: Permissions.CONTENT.FEEDS.PLANNING.BOARD.LIST,
      },
      {
        id: 'content-feeds-list',
        title: <IntlMessages id="ui-general-catalog" />,
        navLink: `${appRoutes.urlPageFeeds({ feedSource: FEEDS_SOURCE_MAP.LIST })}?search=released_to%3A${moment().format(DATE_FORMAT)}`,
        permission: Permissions.CONTENT.FEEDS.LIST,
      },
      {
        id: 'content-feeds-released-list',
        title: <IntlMessages id="sidebar-content-feeds-released" />,
        navLink: `${appRoutes.urlPageFeeds({ feedSource: FEEDS_SOURCE_MAP.RELEASED })}?search=released_to%3A${moment().format(DATE_FORMAT)}`,
        permission: Permissions.CONTENT.FEEDS.RELEASED,
      },
      {
        id: 'content-feeds-archived-list',
        title: <IntlMessages id="sidebar-content-feeds-archived" />,
        navLink: appRoutes.urlPageFeeds({ feedSource: FEEDS_SOURCE_MAP.ARCHIVED }),
        permission: Permissions.CONTENT.FEEDS.ARCHIVED,
      },
      {
        id: 'content-feeds-trashed-list',
        title: <IntlMessages id="sidebar-content-feeds-trashed" />,
        navLink: appRoutes.urlPageFeeds({ feedSource: FEEDS_SOURCE_MAP.TRASHED }),
        permission: Permissions.CONTENT.FEEDS.TRASHED,
      },
    ],
  },
  {
    id: 'content-images',
    title: <IntlMessages id="sidebar-content-images" />,
    icon: <PiImagesSquare size={18} />,
    groupPermissions: [
      Permissions.CONTENT.IMAGES.LIST,
      Permissions.CONTENT.IMAGES.RESERVE_STATISTICS.LIST,
      Permissions.CONTENT.IMAGES.SEARCH_SIMILAR,
    ],
    children: [
      {
        id: 'content-images-index',
        title: <IntlMessages id="ui-general-catalog" />,
        navLink: appRoutes.urlPageImages,
        permission: Permissions.CONTENT.IMAGES.LIST,
      },
      {
        id: 'content-content-reserve-statistics',
        title: <IntlMessages id="sidebar-content-reserve-statistics" />,
        navLink: appRoutes.urlPageImagesReserveStatistics,
        permission: Permissions.CONTENT.IMAGES.RESERVE_STATISTICS.LIST,
      },
      {
        id: 'content-similar-images',
        title: <IntlMessages id="sidebar-content-images-search-similar" />,
        navLink: appRoutes.urlPageImagesSearchSimilar,
        permission: Permissions.CONTENT.IMAGES.SEARCH_SIMILAR,
      },
    ],
  },
  {
    id: 'content-categories',
    title: <IntlMessages id="sidebar-content-categories" />,
    icon: <BiCategoryAlt size={18} />,
    groupPermissions: [Permissions.CONTENT.CATEGORIES.LIST],
    children: [
      {
        id: 'content-categories-index',
        title: <IntlMessages id="ui-general-list" />,
        navLink: appRoutes.urlPageCategories(),
        permission: Permissions.CONTENT.CATEGORIES.LIST,
      },
      {
        id: 'content-categories-priority',
        title: <IntlMessages id="sidebar-general-priority" />,
        navLink: appRoutes.urlPageCategoriesPriority,
        permission: Permissions.CONTENT.CATEGORIES.PRIORITIES.LIST,
      },
      {
        id: 'content-categories-dictionaries',
        title: <IntlMessages id="ui-general-dictionaries" />,
        navLink: appRoutes.urlPageDictionaries({ urlPrefix: SOURCES_MAP.CATEGORIES, source: SOURCES_MAP.CATEGORIES }),
        permission: Permissions.TRANSLATIONS.DICTIONARIES.LIST.VIEW_PAGE,
      },
    ],
  },
  {
    id: 'content-bonuses',
    title: <IntlMessages id="sidebar-content-bonuses" />,
    icon: <PiGift size={18} />,
    navLink: appRoutes.urlPageBonuses,
    permission: Permissions.CONTENT.BONUSES.LIST,
  },
  {
    id: 'content-achievements',
    title: <IntlMessages id="sidebar-content-achievements" />,
    icon: <PiMedal size={18} />,
    navLink: appRoutes.urlPageAchievements,
    permission: Permissions.CONTENT.ACHIEVEMENTS.LIST,
  },
  {
    id: 'content-collections',
    title: <IntlMessages id="sidebar-content-collections" />,
    icon: <PiPackageLight size={18} />,
    groupPermissions: [Permissions.CONTENT.COLLECTIONS.LIST],
    children: [
      {
        id: 'content-collections-index',
        title: <IntlMessages id="ui-general-list" />,
        navLink: appRoutes.urlPageCollections(),
        permission: Permissions.CONTENT.COLLECTIONS.LIST,
      },
      {
        id: 'collections-priorities',
        title: <IntlMessages id="sidebar-general-priority" />,
        navLink: appRoutes.urlPageCollectionPriority,
        permission: Permissions.CONTENT.COLLECTIONS.PRIORITIES.LIST,
      },
      {
        id: 'collections-dictionaries',
        title: <IntlMessages id="ui-general-dictionaries" />,
        navLink: appRoutes.urlPageDictionaries({
          urlPrefix: SOURCES_MAP.COLLECTIONS,
          source: SOURCES_MAP.COLLECTIONS,
          multipleSources: COLLECTIONS_SOURCES,
        }),
        permission: Permissions.TRANSLATIONS.DICTIONARIES.LIST.VIEW_PAGE,
      },
    ],
  },
  {
    id: 'content-dynamic-news',
    title: <IntlMessages id="sidebar-featured-news" />,
    icon: <PiNewspaperLight size={18} />,
    groupPermissions: [Permissions.CONTENT.NEWS.LIST],
    children: [
      {
        id: 'content-dynamic-news-list',
        title: <IntlMessages id="ui-general-list" />,
        navLink: appRoutes.urlPageDynamicNews,
        permission: Permissions.CONTENT.NEWS.LIST,
      },
      {
        id: 'content-dynamic-news-priority',
        title: <IntlMessages id="sidebar-general-priority" />,
        navLink: appRoutes.urlPageDynamicNewsPriority,
        permission: Permissions.CONTENT.NEWS.PRIORITY,
      },
      {
        id: 'content-dynamic-news-dictionaries',
        title: <IntlMessages id="ui-general-dictionaries" />,
        navLink: appRoutes.urlPageDictionaries({ urlPrefix: SOURCES_MAP.NEWS, source: SOURCES_MAP.NEWS }),
        permission: Permissions.TRANSLATIONS.DICTIONARIES.LIST.VIEW_PAGE,
      },
    ],
  },
  ...(FEATURE_SHOW_OLD_NEWS
    ? [
        {
          id: 'news',
          title: <IntlMessages id="sidebar-featured-old-news" />,
          icon: <PiNewspaperLight size={18} />,
          groupPermissions: [Permissions.CONTENT.NEWS.LIST],
          children: [
            {
              id: 'news-list',
              title: <IntlMessages id="ui-general-list" />,
              navLink: appRoutes.urlPageNews,
              permission: Permissions.CONTENT.NEWS.LIST,
            },
            {
              id: 'news-priority',
              title: <IntlMessages id="sidebar-general-priority" />,
              navLink: appRoutes.urlPageNewsPriority,
              permission: Permissions.CONTENT.NEWS.PRIORITY,
            },
          ],
        },
      ]
    : []),
  {
    header: <IntlMessages id="sidebar-featured" />,
    groupPermissions: [
      Permissions.FEATURED.FEATURES.DAILY.LIST,
      Permissions.FEATURED.FEATURES.EDITOR_CHOICE.LIST,
      Permissions.FEATURED.FEATURES.EXTERNAL_PROMO.LIST,
      Permissions.FEATURED.FEATURES.PRIORITY.LIST,
    ],
  },
  {
    id: 'daily',
    title: <IntlMessages id="sidebar-featured-daily" />,
    icon: <PiCloudSunLight size={18} />,
    groupPermissions: Permissions.FEATURED.FEATURES.DAILY.LIST,
    children: [
      {
        id: 'daily-list',
        title: <IntlMessages id="ui-general-list" />,
        navLink: appRoutes.urlPageFeatures({ featureSource: FEATURES_SOURCE_MAP.DAILY }),
        permission: Permissions.FEATURED.FEATURES.DAILY.LIST,
      },
      {
        id: 'daily-dictionaries',
        title: <IntlMessages id="ui-general-dictionaries" />,
        navLink: appRoutes.urlPageDictionaries({
          urlPrefix: FEATURES_SOURCE_MAP.DAILY,
          source: FEATURES_SOURCE_MAP.DAILY,
        }),
        permission: Permissions.TRANSLATIONS.DICTIONARIES.LIST.VIEW_PAGE,
      },
    ],
  },
  {
    id: 'editors-choice',
    title: <IntlMessages id="sidebar-featured-editors-choice" />,
    icon: <PiSealCheck size={18} />,
    groupPermissions: Permissions.FEATURED.FEATURES.EDITOR_CHOICE.LIST,
    children: [
      {
        id: 'editors-choice-list',
        title: <IntlMessages id="ui-general-list" />,
        navLink: appRoutes.urlPageFeatures({ featureSource: FEATURES_SOURCE_MAP.EDITOR_CHOICE }),
        permission: Permissions.FEATURED.FEATURES.EDITOR_CHOICE.LIST,
      },
      {
        id: 'editors-choice-dictionaries',
        title: <IntlMessages id="ui-general-dictionaries" />,
        navLink: appRoutes.urlPageDictionaries({
          urlPrefix: FEATURES_SOURCE_MAP.EDITOR_CHOICE,
          source: FEATURES_SOURCE_MAP.EDITOR_CHOICE,
        }),
        permission: Permissions.TRANSLATIONS.DICTIONARIES.LIST.VIEW_PAGE,
      },
    ],
  },
  {
    id: 'external-promo',
    title: <IntlMessages id="sidebar-featured-external-promo" />,
    icon: <PiRocketLaunchLight size={18} />,
    groupPermissions: Permissions.FEATURED.FEATURES.EXTERNAL_PROMO.LIST,
    children: [
      {
        id: 'external-promo-list',
        title: <IntlMessages id="ui-general-list" />,
        navLink: appRoutes.urlPageFeatures({ featureSource: FEATURES_SOURCE_MAP.EXTERNAL_PROMO }),
        permission: Permissions.FEATURED.FEATURES.EXTERNAL_PROMO.LIST,
      },
      {
        id: 'external-promo-dictionaries',
        title: <IntlMessages id="ui-general-dictionaries" />,
        navLink: appRoutes.urlPageDictionaries({
          urlPrefix: FEATURES_SOURCE_MAP.EXTERNAL_PROMO,
          source: FEATURES_SOURCE_MAP.EXTERNAL_PROMO,
        }),
        permission: Permissions.TRANSLATIONS.DICTIONARIES.LIST.VIEW_PAGE,
      },
    ],
  },
  {
    id: 'featured-priorities',
    title: <IntlMessages id="sidebar-general-priority" />,
    icon: <PiQueue size={18} />,
    navLink: appRoutes.urlPageFeaturePriorities,
    permission: Permissions.FEATURED.FEATURES.PRIORITY.LIST,
  },
  {
    header: <IntlMessages id="sidebar-notifications" />,
    groupPermissions: [Permissions.OTHER.PUSH_ICONS.LIST.VIEW, Permissions.OTHER.DEEPLINKS.LIST],
  },
  {
    id: 'local-notifications',
    title: <IntlMessages id="sidebar-pages-local-notifications" />,
    icon: <PiEnvelopeLight size={18} />,
    groupPermissions: [
      Permissions.OTHER.NOTIFICATIONS.LOCAL.LIST,
      Permissions.TRANSLATIONS.DICTIONARIES.LIST.VIEW_PAGE,
    ],
    children: [
      {
        id: 'other-local-notifications-list',
        title: <IntlMessages id="ui-general-list" />,
        navLink: appRoutes.urlPageLocalNotifications,
        permission: Permissions.OTHER.NOTIFICATIONS.LOCAL.LIST,
      },
      {
        id: 'other-local-notifications-dictionaries',
        title: <IntlMessages id="ui-general-dictionaries" />,
        navLink: appRoutes.urlPageDictionaries({
          urlPrefix: SOURCES_MAP.LOCAL_NOTIFICATIONS,
          source: SOURCES_MAP.LOCAL_NOTIFICATIONS,
        }),
        permission: Permissions.TRANSLATIONS.DICTIONARIES.LIST.VIEW_PAGE,
      },
    ],
  },
  {
    id: 'push-notifications',
    title: <IntlMessages id="sidebar-pages-push-notifications" />,
    icon: <PiBellRingingLight size={18} />,
    groupPermissions: [
      Permissions.OTHER.NOTIFICATIONS.PUSH.LIST,
      Permissions.TRANSLATIONS.DICTIONARIES.LIST.VIEW_PAGE,
    ],
    children: [
      {
        id: 'other-push-notifications-list',
        title: <IntlMessages id="ui-general-list" />,
        navLink: appRoutes.urlPagePushNotifications,
        permission: Permissions.OTHER.NOTIFICATIONS.PUSH.LIST,
      },
      {
        id: 'other-push-notifications-schedule-list',
        title: <IntlMessages id="sidebar-pages-push-notifications-schedule" />,
        navLink: appRoutes.urlPagePushNotificationsSchedule,
        permission: Permissions.OTHER.NOTIFICATIONS.PUSH.SCHEDULE.LIST,
      },
      {
        id: 'other-push-notifications-dictionaries',
        title: <IntlMessages id="ui-general-dictionaries" />,
        navLink: appRoutes.urlPageDictionaries({
          urlPrefix: SOURCES_MAP.PUSH_NOTIFICATIONS,
          source: SOURCES_MAP.PUSH_NOTIFICATIONS,
        }),
        permission: Permissions.TRANSLATIONS.DICTIONARIES.LIST.VIEW_PAGE,
      },
    ],
  },
  {
    header: <IntlMessages id="sidebar-other" />,
    groupPermissions: [Permissions.OTHER.PUSH_ICONS.LIST.VIEW, Permissions.OTHER.DEEPLINKS.LIST],
  },
  {
    id: 'push-icons',
    title: <IntlMessages id="sidebar-pages-push-icons" />,
    icon: <PiUpload size={18} />,
    navLink: appRoutes.urlPagePushIcons,
    permission: Permissions.OTHER.PUSH_ICONS.LIST.VIEW,
  },
  {
    id: 'deeplinks',
    title: <IntlMessages id="sidebar-pages-deeplinks" />,
    icon: <PiLink size={18} />,
    navLink: appRoutes.urlPageDeeplinks,
    permission: Permissions.OTHER.DEEPLINKS.LIST,
  },
  {
    id: 'popups',
    title: <IntlMessages id="sidebar-pages-popups" />,
    icon: <PiPaperPlaneTilt size={18} />,
    groupPermissions: [
      Permissions.OTHER.POPUPS.LIST,
      Permissions.OTHER.POPUPS.PRIORITY.LIST,
      Permissions.TRANSLATIONS.DICTIONARIES.LIST.VIEW_PAGE,
    ],
    children: [
      {
        id: 'popups-list',
        title: <IntlMessages id="sidebar-general-list" />,
        navLink: appRoutes.urlPagePopups(),
        permission: Permissions.OTHER.POPUPS.LIST,
      },
      {
        id: 'popups-priorities',
        title: <IntlMessages id="sidebar-general-priority" />,
        navLink: appRoutes.urlPagePopupsPriority,
        permission: Permissions.OTHER.POPUPS.PRIORITY.LIST,
      },
      {
        id: 'popups-dictionaries',
        title: <IntlMessages id="ui-general-dictionaries" />,
        navLink: appRoutes.urlPageDictionaries({ urlPrefix: SOURCES_MAP.POPUPS, source: SOURCES_MAP.POPUPS }),
        permission: Permissions.TRANSLATIONS.DICTIONARIES.LIST.VIEW_PAGE,
      },
    ],
  },
  {
    id: 'events',
    title: <IntlMessages id="sidebar-pages-events" />,
    icon: <PiConfettiLight size={18} />,
    groupPermissions: [
      Permissions.OTHER.EVENTS.LIST,
      Permissions.TRANSLATIONS.DICTIONARIES.LIST.VIEW_PAGE,
      Permissions.CONTENT.COLLECTIONS.LIST,
      Permissions.OTHER.POPUPS.LIST,
      Permissions.FEATURED.FEATURES.EXTERNAL_PROMO.LIST,
    ],
    children: [
      {
        id: 'events-list',
        title: <IntlMessages id="sidebar-general-list" />,
        navLink: appRoutes.urlPageEvents,
        permission: Permissions.OTHER.EVENTS.LIST,
      },
      {
        id: 'events-collections',
        title: <IntlMessages id="sidebar-content-collections" />,
        navLink: appRoutes.urlPageCollections({ urlPrefix: SOURCES_MAP.EVENTS, source: EVENT_TYPES_MAP.EVENT }),
        permission: Permissions.CONTENT.COLLECTIONS.LIST,
      },
      {
        id: 'events-external-promo-list',
        title: <IntlMessages id="sidebar-featured-external-promo" />,
        navLink: appRoutes.urlPageFeatures({
          urlPrefix: SOURCES_MAP.EVENTS,
          featureType: EVENT_TYPES_MAP.EVENT,
          featureSource: FEATURES_SOURCE_MAP.EXTERNAL_PROMO,
        }),
        permission: Permissions.FEATURED.FEATURES.EXTERNAL_PROMO.LIST,
      },
      {
        id: 'events-popups',
        title: <IntlMessages id="sidebar-pages-popups" />,
        navLink: appRoutes.urlPagePopups({ urlPrefix: SOURCES_MAP.EVENTS, source: EVENT_TYPES_MAP.EVENT }),
        permission: Permissions.OTHER.POPUPS.LIST,
      },
      {
        id: 'events-dictionaries',
        title: <IntlMessages id="ui-general-dictionaries" />,
        navLink: appRoutes.urlPageDictionaries({ urlPrefix: SOURCES_MAP.EVENTS, source: EVENTS_DICT_SOURCE }),
        permission: Permissions.TRANSLATIONS.DICTIONARIES.LIST.VIEW_PAGE,
      },
    ],
  },
  {
    id: 'stickers',
    title: <IntlMessages id="sidebar-pages-stickers" />,
    icon: <BsStickies size={18} />,
    groupPermissions: [Permissions.OTHER.STICKERS.LIST, Permissions.TRANSLATIONS.DICTIONARIES.LIST.VIEW_PAGE],
    children: [
      {
        id: 'stickers-list',
        title: <IntlMessages id="sidebar-general-list" />,
        navLink: appRoutes.urlPageStickers,
        permission: Permissions.OTHER.STICKERS.LIST,
      },
      {
        id: 'stickers-dictionaries',
        title: <IntlMessages id="ui-general-dictionaries" />,
        navLink: appRoutes.urlPageDictionaries({ urlPrefix: SOURCES_MAP.STICKERS, source: SOURCES_MAP.STICKERS }),
        permission: Permissions.TRANSLATIONS.DICTIONARIES.LIST.VIEW_PAGE,
      },
    ],
  },
  {
    id: 'static-content-reset',
    title: <IntlMessages id="sidebar-pages-reset-static-content" />,
    icon: <PiArrowCounterClockwise size={18} />,
    navLink: appRoutes.urlPageResetStaticContentLogs,
    permission: Permissions.OTHER.STATIC_CONTENT_RESET.LIST,
  },
  {
    header: <IntlMessages id="sidebar-pages-filter-constructors" />,
    groupPermissions: [
      Permissions.FILTER_CONSTRUCTORS.FILTERS.LIST,
      Permissions.FILTER_CONSTRUCTORS.GROUPS.LIST,
      Permissions.FILTER_CONSTRUCTORS.COLLECTIONS.LIST,
    ],
  },
  {
    id: 'filters-constructor-filter',
    title: <IntlMessages id="sidebar-pages-filters" />,
    icon: <PiListMagnifyingGlassLight size={18} />,
    groupPermissions: [
      Permissions.FILTER_CONSTRUCTORS.FILTERS.LIST,
      Permissions.TRANSLATIONS.DICTIONARIES.LIST.VIEW_PAGE,
    ],
    children: [
      {
        id: 'filters-list',
        title: <IntlMessages id="sidebar-general-list" />,
        navLink: appRoutes.urlPageFilters,
        permission: Permissions.FILTER_CONSTRUCTORS.FILTERS.LIST,
      },
      {
        id: 'filter-groups-dictionaries',
        title: <IntlMessages id="ui-general-dictionaries" />,
        navLink: appRoutes.urlPageDictionaries({ urlPrefix: SOURCES_MAP.FILTERS, source: SOURCES_MAP.FILTERS }),
        permission: Permissions.TRANSLATIONS.DICTIONARIES.LIST.VIEW_PAGE,
      },
    ],
  },
  {
    id: 'filters-constructor-filter-groups',
    title: <IntlMessages id="sidebar-pages-filters-groups" />,
    icon: <PiCirclesThreePlusLight size={18} />,
    groupPermissions: [
      Permissions.FILTER_CONSTRUCTORS.GROUPS.LIST,
      Permissions.TRANSLATIONS.DICTIONARIES.LIST.VIEW_PAGE,
    ],
    children: [
      {
        id: 'filter-groups-list',
        title: <IntlMessages id="sidebar-general-list" />,
        navLink: appRoutes.urlPageFilterGroups,
        permission: Permissions.FILTER_CONSTRUCTORS.GROUPS.LIST,
      },
      {
        id: 'filter-group-dictionaries',
        title: <IntlMessages id="ui-general-dictionaries" />,
        navLink: appRoutes.urlPageDictionaries({
          urlPrefix: SOURCES_MAP.FILTER_GROUPS,
          source: SOURCES_MAP.FILTER_GROUPS,
        }),
        permission: Permissions.TRANSLATIONS.DICTIONARIES.LIST.VIEW_PAGE,
      },
    ],
  },
  {
    id: 'filters-constructor-filter-collections',
    title: <IntlMessages id="sidebar-pages-filters-collections" />,
    icon: <VscNotebookTemplate size={14} />,
    navLink: appRoutes.urlPageFilterCollections,
    permission: Permissions.FILTER_CONSTRUCTORS.COLLECTIONS.LIST,
  },
  {
    header: <IntlMessages id="sidebar-template-constructors" />,
    groupPermissions: [Permissions.TEMPLATE_CONSTRUCTORS.TEMPLATES.LIST],
  },
  {
    id: 'template-constructors',
    title: <IntlMessages id="sidebar-templates" />,
    icon: <PiPaperPlaneTilt size={18} />,
    groupPermissions: [
      Permissions.TEMPLATE_CONSTRUCTORS.TEMPLATES.LIST,
      Permissions.TEMPLATE_CONSTRUCTORS.TEMPLATES.VISUALIZATIONS.LIST,
    ],
    children: [
      {
        id: 'templates-list',
        title: <IntlMessages id="sidebar-general-list" />,
        icon: <ImInsertTemplate size={14} />,
        navLink: appRoutes.urlPageTemplates(),
        permission: Permissions.TEMPLATE_CONSTRUCTORS.TEMPLATES.LIST,
      },
      {
        id: 'templates-visualizations',
        title: <IntlMessages id="sidebar-templates-visualizations" />,
        navLink: appRoutes.urlPageTemplatesVisualizations,
        permission: Permissions.TEMPLATE_CONSTRUCTORS.TEMPLATES.VISUALIZATIONS.LIST,
      },
    ],
  },
  {
    id: 'presets',
    title: <IntlMessages id="sidebar-presets" />,
    icon: <VscNotebookTemplate size={14} />,
    navLink: appRoutes.urlPagePresets(),
    permission: Permissions.TEMPLATE_CONSTRUCTORS.PRESETS.LIST,
  },
  {
    header: <IntlMessages id="sidebar-translations" />,
    groupPermissions: [
      Permissions.TRANSLATIONS.LANGUAGES.LANGUAGES_LIST.VIEW_PAGE,
      Permissions.TRANSLATIONS.STRUCTURES.LIST.VIEW_PAGE,
    ],
  },
  {
    id: 'languages',
    title: <IntlMessages id="ui-general-languages" />,
    icon: <IoEarth size={18} />,
    navLink: appRoutes.urlPageLanguages,
    permission: Permissions.TRANSLATIONS.LANGUAGES.LANGUAGES_LIST.VIEW_PAGE,
  },
  {
    id: 'structures',
    title: <IntlMessages id="ui-general-structures" />,
    icon: <PiTreeStructure size={18} />,
    navLink: appRoutes.urlPageStructures,
    permission: Permissions.TRANSLATIONS.STRUCTURES.LIST.VIEW_PAGE,
  },
  {
    id: 'dictionaries',
    title: <IntlMessages id="ui-general-dictionaries" />,
    icon: <PiTranslate size={18} />,
    navLink: appRoutes.urlPageDictionaries(),
    permission: Permissions.TRANSLATIONS.DICTIONARIES.LIST.VIEW_PAGE,
  },
];

export default pages;
